import { compareDesc, parseISO } from 'date-fns'
import { useQueryClient } from '@tanstack/react-query'

import { createInstance } from '@/helpers/api/base'
import { useAppMutation } from '@/helpers/api/mutation'
import { GET_ROUTE_DETAILS_KEY } from './useGetRouteDetails'
import { ISendRouteStrategyRequest, RouteDetails } from '@/types/routeDetails'
import { useGetUserInfo } from '@/modules/Auth'
import { formatRouteStrategyRequestBody } from '../helpers/formatRouteStrategyRequestBody'

export const useSendRouteStrategy = (route: string) => {
    const { data: userInfo } = useGetUserInfo()
    const queryClient = useQueryClient()

    const sendRouteStrategyMutation = useAppMutation<void, Error, ISendRouteStrategyRequest, RouteDetails>({
        mutationFn: (data) =>
            createInstance({
                url: '/routes/strategy',
                method: 'POST',
                data,
            }),
        queryKey: [GET_ROUTE_DETAILS_KEY, route],
        optimisticChangesCallback: (requestBody, previousData) => {
            const newStrategy = formatRouteStrategyRequestBody(requestBody, userInfo)

            if (!previousData || !newStrategy) {
                return previousData
            }

            return {
                ...previousData,
                strategies: [...(previousData.strategies || []), newStrategy].sort((a, b) =>
                    compareDesc(parseISO(a?.timestamp), parseISO(b.timestamp))
                ),
            }
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['AREAS'], exact: false })
        },
    })

    const handleSendRouteStrategy = (strategy: string) => {
        sendRouteStrategyMutation.mutate({ route, strategy })
    }

    return { handleSendRouteStrategy }
}
